import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { publishCms } from "@/services/cmsApi";
import { CmsPublishGate } from "./CmsPublishGate";
import { VersionsSheet } from "./VersionsSheet";

const POLICIES = [
  ["privacy", "Privacy Policy", "/policies/privacy"],
  ["terms", "Terms & Conditions", "/policies/terms"],
  ["cancellation-refund", "Cancellation & Refund Policy", "/policies/cancellation-refund"],
  ["shipping", "Shipping Policy", "/policies/shipping"],
];
const build = (live) => Object.fromEntries(POLICIES.map(([k, t]) => [k, { title: live?.[k]?.title || t, body: live?.[k]?.body || "" }]));

// site_content/policies: { privacy|terms|cancellation-refund|shipping: { title, body } }. Blank line = paragraph.
export function PoliciesEditor({ live }) {
  const base = useMemo(() => build(live), [live]);
  const [draft, setDraft] = useState(base);
  const [active, setActive] = useState("privacy");
  const [gate, setGate] = useState(null);
  const [ver, setVer] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(base);
  useEffect(() => { if (!dirty) setDraft(base); }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = draft[active];
  const set = (patch) => setDraft({ ...draft, [active]: { ...cur, ...patch } });
  const publish = () => setGate({
    title: "Publish policy pages", description: "Updates /policies/* on the website immediately. Requires the profile password and an OTP sent to",
    run: async (auth) => { await publishCms({ ...auth, target: "site_content", doc_id: "policies", data: draft }); toast.success("Policies published"); },
  });
  return (
    <div data-testid="policies-editor">
      {!live && <p className="mb-3 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2">site_content/policies doesn't exist yet — the website shows "being finalised" until you publish.</p>}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {POLICIES.map(([k, t]) => (
          <Button key={k} size="sm" variant={k === active ? "default" : "outline"} className="h-8" onClick={() => setActive(k)} data-testid={`policy-tab-${k}`}>
            {t}{draft[k].body ? "" : " ·  empty"}
          </Button>
        ))}
        <div className="ml-auto flex gap-2">
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDraft(base)} data-testid="policies-discard">Discard</Button>}
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setVer(true)} data-testid="policies-versions-btn"><Icons.History className="h-4 w-4" /> Versions</Button>
          <Button size="sm" className="gap-1.5" disabled={!dirty} onClick={publish} data-testid="policies-publish-btn"><Icons.UploadCloud className="h-4 w-4" /> Publish</Button>
        </div>
      </div>
      <Card className="p-5 space-y-3">
        <p className="text-[11px] font-mono text-muted-foreground">brugo.in{POLICIES.find((p) => p[0] === active)[2]}</p>
        <div className="space-y-1.5"><Label>Title</Label><Input value={cur.title} onChange={(e) => set({ title: e.target.value })} data-testid="policy-title" /></div>
        <div className="space-y-1.5"><Label>Body (plain text — leave a blank line between paragraphs)</Label>
          <Textarea value={cur.body} onChange={(e) => set({ body: e.target.value })} rows={18} className="font-mono text-xs" data-testid="policy-body" />
        </div>
        <p className="text-[11px] text-muted-foreground">{cur.body.split(/\n\s*\n/).filter((x) => x.trim()).length} paragraphs · Footer on site: © {new Date().getFullYear()} Nexorigo Ventures Private Limited · CIN: U63120HR2026PTC147231</p>
      </Card>
      <CmsPublishGate gate={gate} onClose={() => setGate(null)} />
      <VersionsSheet target="site_content" docId="policies" open={ver} onClose={() => setVer(false)} />
    </div>
  );
}
